import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Card, Select, Space, message } from "antd";
import {
  DownloadOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { caseAPI, matriculaAPI } from "../services/api";
import MatriculaPlanillaTable, {
  MatriculaRow,
} from "../components/MatriculaPlanillaTable";

const PlanillaPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const caseParam = searchParams.get("case");
  const [cases, setCases] = useState<any[]>([]);
  const [rows, setRows] = useState<MatriculaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const caseId = caseParam ? parseInt(caseParam, 10) : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await matriculaAPI.list(caseId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || "No se pudieron cargar matrículas");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void caseAPI
      .getAll()
      .then((data) => setCases(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const title = useMemo(() => {
    if (!caseId) return "Planilla de matrículas";
    const c = cases.find((x) => x.case_id === caseId);
    return c
      ? `Planilla — ${c.case_number || `Caso ${caseId}`}`
      : `Planilla — Caso ${caseId}`;
  }, [caseId, cases]);

  async function handleExtract() {
    if (!caseId) {
      message.info("Seleccione un caso para re-extraer");
      return;
    }
    setExtracting(true);
    try {
      const res = await matriculaAPI.extractCase(caseId);
      message.success(
        `Extraídas ${res.upserted} matrículas (${res.scanned} docs, ${res.skipped_not_folio} no folio)`
      );
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || "Error al extraer");
    } finally {
      setExtracting(false);
    }
  }

  return (
    <div>
      <Card
        title={title}
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder="Filtrar por caso"
              style={{ minWidth: 220 }}
              value={caseId}
              options={cases.map((c) => ({
                value: c.case_id,
                label: `${c.case_number || c.case_id} — ${c.summary || c.case_type || ""}`.slice(
                  0,
                  60
                ),
              }))}
              onChange={(v) => {
                if (v == null) setSearchParams({});
                else setSearchParams({ case: String(v) });
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              Actualizar
            </Button>
            <Button
              icon={<SyncOutlined />}
              loading={extracting}
              disabled={!caseId}
              onClick={() => void handleExtract()}
            >
              Re-extraer caso
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={matriculaAPI.getXlsxUrl(caseId)}
            >
              Descargar XLSX
            </Button>
          </Space>
        }
      >
        <MatriculaPlanillaTable
          rows={rows}
          loading={loading}
          showCaseColumn={!caseId}
          emptyText="Sin matrículas. Elija un caso y pulse Re-extraer, o ejecute el extract bulk."
          onMatriculaUpdated={(row) =>
            setRows((prev) =>
              prev.map((r) =>
                r.matricula_id === row.matricula_id ? { ...r, ...row } : r
              )
            )
          }
        />
      </Card>
    </div>
  );
};

export default PlanillaPage;
